// ABOUTME: The dashboard grid: links and widgets in columns, tag grouping, and the sponsored card.
// ABOUTME: Drag-and-drop reorders in edit mode and writes the full item list back.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../hooks";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import Scratchpad from "./Scratchpad";
import EventsOnThisDayCard from "./EventsOnThisDayCard";
import NPRWidget from "./NPRWidget";
// import DarkModeToggle from "./DarkModeToggle";
import AppCard from "./AppCard";
import ErrorBoundary from "./ErrorBoundary";
import SearchWidget from "./SearchWidget";
import EditLinkModal from "./EditLinkModal";
import QuickTodo from "./QuickTodo";
import FTUXCard from "./FTUXCard"; // Import FTUXCard
import NASAPicOfDayWidget from "./NASAPicOfDayWidget"; // Import NASAPicOfDayWidget
// Remove Header import
import { useActiveAd } from "../hooks/useAds";
import { trackAdImpression } from "../services/adAnalytics";
import { limitsService } from "../services/limitsService";
import { reorderDashboardItems } from "../utils/dashboardReorder";
import { groupItems, readCollapsed, writeCollapsed } from "../utils/grouping";
import { useUIStore } from "../stores/ui";
import { useDashboardStore } from "../stores/dashboard";

// --- Types ---
import type { DashboardItem } from "../services/dashboardService";
import type { UrlData } from "../services/firebase";

interface StartDashboardProps {
  // Adding links and installing widgets now happen entirely in the Customize
  // sheet, which the app shell mounts — this component no longer needs those
  // callbacks. It still removes widgets via the per-card control.
  onWidgetRemove?: (name: string) => void;
  onDeleteItem?: (id: string) => void;
  onEditLink?: (id: string, updated: Partial<UrlData>) => void;
  /**
   * The complete dashboard item list — never a filtered subset. Drag-and-drop
   * persists whatever it is handed, so passing a filtered view here would
   * delete every item left out of it on the first drag.
   */
  dashboardItems: DashboardItem[];
  setDashboardItems: (items: DashboardItem[]) => Promise<void>;
  loading?: boolean;
  /** The dashboard subscription failed; shown in place of the empty state. */
  loadError?: Error | null;
  columnCount: number;
}

// --- Type guard for UrlData ---
function isUrlData(item: unknown): item is UrlData {
  if (typeof item !== "object" || item === null) return false;
  const fields = item as Record<string, unknown>;
  return (
    fields.type === "link" &&
    typeof fields.url === "string" &&
    typeof fields.order === "number" &&
    typeof fields.col === "number"
  );
}

// --- Widget Registry ---
type WidgetRegistry = { [key: string]: { component: React.FC; label: string } };

// Each widget paints its own box; `label` is the mono-caps heading above it.
const widgetRegistry: WidgetRegistry = {
  Scratchpad: { component: Scratchpad, label: "Scratchpad" },
  "Quick To-do List": { component: QuickTodo, label: "To-do" },
  DatesWiki: { component: EventsOnThisDayCard, label: "On this day" },
  "NPR Hourly News": { component: NPRWidget, label: "NPR News" },
  NASAPicOfDay: { component: NASAPicOfDayWidget, label: "NASA · Picture of the day" },
  SearchWidget: { component: SearchWidget, label: "Search" },
};

function StartDashboard({
  onWidgetRemove,
  onDeleteItem,
  onEditLink,
  dashboardItems,
  setDashboardItems,
  loading: loadingProp,
  loadError = null,
  columnCount,
}: StartDashboardProps) {
  const { user } = useAuth();
  const [editData, setEditData] = useState<DashboardItem | null>(null);
  // Edit mode is toggled from the header; the grid only reads it.
  const editing = useUIStore((s) => s.editing);
  const spacing = useUIStore((s) => s.spacing);
  const groupingEnabled = useUIStore((s) => s.groupingEnabled);
  // The Customize sheet is a sibling, not a child — coordinate through the store.
  const openSheet = useDashboardStore((s) => s.openSheet);
  const justAddedWidget = useDashboardStore((s) => s.justAddedWidget);

  // Map columnCount to Tailwind grid-cols class
  const gridColsClass =
    {
      2: "md:grid-cols-2",
      3: "md:grid-cols-3",
      4: "md:grid-cols-4",
      5: "md:grid-cols-5",
    }[columnCount] || "md:grid-cols-3";

  // Spacing tokens
  const gridGap = { compact: "gap-2", default: "gap-6", relaxed: "gap-10" }[spacing];
  const colGap = { compact: "gap-0.5", default: "gap-1.5", relaxed: "gap-3" }[spacing];

  // --- Persistable set ---
  // The authoritative list that drag-and-drop reads from and writes back.
  // Ads are stripped because they are injected for display only and must never
  // reach Firestore. Malformed links (no URL or no position) are deliberately
  // KEPT: they are hidden from view below, but silently dropping them here
  // would make a drag delete them. "Delete all links" in Settings removes them
  // along with everything else.
  const persistableItems = useMemo(
    () => dashboardItems.filter((item) => !item.isAd),
    [dashboardItems]
  );

  // --- Display set: hide malformed links ---
  const visibleItems = useMemo(
    () =>
      persistableItems.filter((item) => {
        if (item.type !== "link") return true;
        return (
          typeof item.url === "string" &&
          item.url.trim() !== "" &&
          typeof item.order === "number" &&
          typeof item.col === "number"
        );
      }),
    [persistableItems]
  );

  // --- Sponsored link (display only; see docs/ads.md) ---
  // Tiers with ad removal never subscribe. The pick is stable across
  // snapshots so the card's id (and so its Draggable key) doesn't churn.
  const showAds = !limitsService.getUserLimits().canRemoveAds;
  const activeAd = useActiveAd(showAds);

  useEffect(() => {
    if (activeAd) trackAdImpression(activeAd);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one impression per ad id; a re-fired snapshot hands back a new object for the same ad
  }, [activeAd?.id]);

  const adCard = useMemo<DashboardItem | null>(() => {
    if (!activeAd) return null;
    return {
      id: `ad-${activeAd.id}`,
      type: "link",
      url: activeAd.url,
      name: activeAd.title,
      description: activeAd.description || "",
      order: Number.MAX_SAFE_INTEGER,
      col: 0,
      isAd: true,
    };
  }, [activeAd]);

  // --- Column projection ---
  // Bucket once instead of running filter+sort per column on every render.
  // Items whose col is out of range are surfaced in column 0 rather than
  // silently vanishing (which is what happens when the column count shrinks).
  const columns = useMemo(() => {
    const cols: DashboardItem[][] = Array.from({ length: columnCount }, () => []);
    for (const item of visibleItems) {
      const target =
        typeof item.col === "number" && item.col >= 0 && item.col < columnCount ? item.col : 0;
      cols[target].push(item);
    }
    for (const col of cols) col.sort((a, b) => a.order - b.order);
    // Appended after sorting, and only into the display projection.
    if (adCard && cols.length > 0) {
      const duplicate = (i: DashboardItem) => i.url === adCard.url || i.id === adCard.id;
      if (!visibleItems.some(duplicate)) cols[0].push(adCard);
    }
    return cols;
  }, [visibleItems, columnCount, adCard]);

  // --- Tag grouping (display only) ---
  // Grouping reorders items relative to their stored order, so it stays off
  // while editing: the indices a drop reports are indices into the rendered
  // column, and they must line up with the stored order a reorder writes back.
  const grouped = groupingEnabled && !editing;

  // The grid is mounted under a signed-in shell or the guest shell, never
  // across a sign-in, so the key this is read under holds for the whole mount.
  const userId = user?.uid ?? null;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(userId));

  const toggleGroup = useCallback(
    (tag: string) => {
      // Keyed on the case-folded tag because "Dev" and "dev" are one group.
      const key = tag.toLowerCase();
      // Tags no item carries any more are dropped here rather than on read,
      // since on mount the items may not have loaded yet.
      const live = new Set(
        groupItems(columns.flat()).flatMap((g) => (g.tag ? [g.tag.toLowerCase()] : []))
      );
      const next = new Set([...collapsed].filter((k) => live.has(k)));
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setCollapsed(next);
      writeCollapsed(userId, next);
    },
    [collapsed, columns, userId]
  );

  // The ad is held out of grouping: it carries no tags, so it would land in
  // the unlabeled group and show at the very top of column one.
  const columnGroups = useMemo(() => {
    if (!grouped) return null;
    return columns.map((col) => ({
      groups: groupItems(col.filter((item) => !item.isAd)),
      ads: col.filter((item) => item.isAd),
    }));
  }, [grouped, columns]);

  // Stable identities so AppCard's React.memo actually holds. Previously these
  // were fresh arrows on every render, which made the memo a no-op.
  const handleCardEdit = useCallback((app: UrlData) => {
    setEditData({ ...app, type: "link" } as DashboardItem);
  }, []);

  const handleCardDelete = useCallback(
    async (id: string) => {
      if (onDeleteItem) await onDeleteItem(id);
    },
    [onDeleteItem]
  );

  // --- Drag and Drop ---
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, draggableId } = result;
      if (!destination) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      // Always reorder against the full persistable set, never the rendered
      // subset — the result is written back as a whole-document replace.
      const next = reorderDashboardItems(persistableItems, {
        sourceCol: parseInt(source.droppableId.replace("col-", ""), 10),
        destCol: parseInt(destination.droppableId.replace("col-", ""), 10),
        destIndex: destination.index,
        draggableId,
      });
      if (!next) return;

      // The hook records the failure for the toast in AuthenticatedApp; the
      // catch here keeps a rejected write from becoming an unhandled rejection
      // that nothing displays. The grid re-renders from props, so the card
      // simply snaps back.
      setDashboardItems(next).catch((err) => {
        console.error("Reorder failed:", err);
      });
    },
    [persistableItems, setDashboardItems]
  );

  // `index` is the item's position among the rendered draggables of its
  // column, which dnd requires to match DOM order.
  const renderItem = (item: DashboardItem, index: number) => {
    const isLink = item.type === "link" && isUrlData(item);
    return (
      <Draggable
        draggableId={item.id}
        index={index}
        key={item.id}
        isDragDisabled={!!item.isAd || !editing}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            data-item-id={item.id}
            {...provided.draggableProps}
            // Only a drag handle in edit mode. Spreading this
            // unconditionally added a second tabIndex={0} to
            // every card, so tabbing gave two stops per card and
            // Space started a drag instead of opening the link.
            // A link row drags from its own grab handle instead.
            {...(editing && !isLink ? provided.dragHandleProps || {} : {})}
            style={{
              ...provided.draggableProps.style,
              opacity: snapshot.isDragging ? 0.7 : 1,
            }}
            className={columnCount === 5 ? "col-span-2 w-full" : "w-full"}
          >
            {item.type === "link" && isUrlData(item) ? (
              <ErrorBoundary fallback={<ItemFallback />}>
                <AppCard
                  app={item}
                  editing={editing}
                  dragHandleProps={editing ? provided.dragHandleProps : undefined}
                  onEdit={handleCardEdit}
                  onDelete={handleCardDelete}
                />
              </ErrorBoundary>
            ) : item.type === "widget" ? (
              <WidgetCard
                item={item}
                editing={editing}
                justAdded={justAddedWidget === item.widgetName}
                onRemove={() => {
                  if (onWidgetRemove && item.widgetName) onWidgetRemove(item.widgetName);
                }}
                widgetRegistry={widgetRegistry}
              />
            ) : null}
          </div>
        )}
      </Draggable>
    );
  };

  const renderColumn = (colIdx: number) => {
    const plan = columnGroups?.[colIdx];
    if (!plan) return (columns[colIdx] ?? []).map((item, idx) => renderItem(item, idx));

    let index = 0;
    const sections = plan.groups.map((group) => {
      const tag = group.tag;
      const key = tag === null ? null : tag.toLowerCase();
      const isCollapsed = key !== null && collapsed.has(key);
      return (
        <div key={key ?? "__untagged"} className={`flex flex-col ${colGap}`}>
          {tag !== null && (
            <h2 className="mb-0.5 pb-1 border-b border-[var(--color-border)]">
              <button
                type="button"
                aria-expanded={!isCollapsed}
                onClick={() => toggleGroup(tag)}
                className="w-full flex items-center gap-1.5 px-1 text-left rounded-[var(--radius-sm)]
                           focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              >
                <svg
                  aria-hidden="true"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`shrink-0 text-[var(--color-text-tertiary)] transition-transform
                              ${isCollapsed ? "-rotate-90" : ""}`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
                <span className="truncate min-w-0 text-xs uppercase tracking-[0.12em] font-mono text-[var(--color-text-secondary)]">
                  {tag}
                </span>
                <span
                  aria-hidden="true"
                  className="ml-auto text-xs font-mono text-[var(--color-text-tertiary)]"
                >
                  {group.items.length}
                </span>
              </button>
            </h2>
          )}
          {!isCollapsed && group.items.map((item) => renderItem(item, index++))}
        </div>
      );
    });

    return [...sections, ...plan.ads.map((item) => renderItem(item, index++))];
  };

  // --- Render ---
  return (
    <div className="container mx-auto px-4 py-8 bg-[var(--color-bg)] min-h-screen rounded-[var(--radius-lg)] transition-colors relative">
      <h1 className="sr-only">Dashboard</h1>
      {/* An empty grid means one of three things, and they must not be
          confused: still loading (skeleton), failed to load (error), or
          genuinely empty (first-run card). On a fresh device the first
          snapshot used to render as "Your dashboard is empty". */}
      {visibleItems.length === 0 && loadingProp && (
        <div
          role="status"
          aria-label="Loading your dashboard"
          className={`grid grid-cols-1 ${gridColsClass} ${gridGap} animate-pulse`}
        >
          {[...Array(columnCount)].map((_, colIdx) => (
            <div key={colIdx} className={`flex flex-col ${colGap}`}>
              {[0, 1].map((row) => (
                <div
                  key={row}
                  className="h-20 rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] border border-[var(--color-border)]"
                />
              ))}
            </div>
          ))}
        </div>
      )}
      {visibleItems.length === 0 && !loadingProp && loadError && (
        <div
          role="alert"
          className="w-full rounded-[var(--radius-lg)] bg-[var(--color-bg-secondary)] border border-[color-mix(in_srgb,var(--color-danger)_40%,transparent)] p-5 sm:p-6 flex flex-col items-center text-center gap-3"
        >
          <h2 className="text-lg font-semibold font-heading text-[var(--color-danger)]">
            Couldn't load your dashboard
          </h2>
          <p className="text-sm text-[var(--color-text-tertiary)] break-words max-w-prose">
            {loadError.message}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-1 px-4 py-2 rounded-[var(--radius-sm)] bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-text-inverse)] text-[0.8125rem] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
          >
            Try again
          </button>
        </div>
      )}
      {visibleItems.length === 0 && !loadingProp && !loadError && (
        <FTUXCard onAddClick={() => openSheet("Links")} />
      )}
      <EditLinkModal
        open={!!editData && editData.type === "link"}
        editData={editData && isUrlData(editData) ? editData : null}
        loading={loadingProp}
        onSave={async (link) => {
          if (editData && onEditLink) {
            await onEditLink(editData.id, link);
          }
          setEditData(null);
        }}
        onClose={() => setEditData(null)}
      />
      {/* The Customize sheet is mounted once by the parent app shell. This
          component only asks for it to open — it used to mount a second,
          divergent copy with a different feature set. */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={`grid grid-cols-1 ${gridColsClass} ${gridGap}`}>
          {[...Array(columnCount)].map((_, colIdx) => (
            <Droppable droppableId={`col-${colIdx}`} key={colIdx}>
              {(provided, dropSnapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  // A column being dragged over says so: borderless rows give
                  // the eye nothing else to read the drop target from.
                  className={`flex flex-col ${colGap} min-h-[100px] rounded-[4px] transition-colors
                              ${dropSnapshot.isDraggingOver ? "outline-dashed outline-1 outline-offset-4 outline-[var(--color-accent)] bg-[var(--color-bg-hover)]" : ""}`}
                >
                  {renderColumn(colIdx)}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
      <style>{`
        @keyframes box-shimmer {
          0% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(56, 189, 248, 0); }
          100% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); }
        }
      `}</style>
    </div>
  );
}

// Stands in for one link or widget that threw while rendering, so the rest of
// the grid stays usable.
function ItemFallback() {
  return (
    <div className="h-8 flex items-center px-2 text-sm text-[var(--color-text-tertiary)]">
      Couldn't show this item
    </div>
  );
}

// Widget card wrapper — shows remove badge in edit mode
function WidgetCard({
  item,
  editing,
  justAdded,
  onRemove,
  widgetRegistry,
}: {
  item: DashboardItem;
  editing: boolean;
  justAdded: boolean;
  onRemove: () => void;
  widgetRegistry: WidgetRegistry;
}) {
  const [confirming, setConfirming] = useState(false);
  const entry = item.widgetName ? widgetRegistry[item.widgetName] : undefined;

  return (
    <div>
      {entry && (
        <h2 className="px-1 pb-1 text-xs uppercase tracking-[0.12em] font-mono text-[var(--color-text-secondary)]">
          {entry.label}
        </h2>
      )}
      <div
        className={
          "relative transition-shadow overflow-hidden rounded-[var(--radius-md)] " +
          (justAdded
            ? "animate-[box-shimmer_1.2s] ring-2 ring-[color-mix(in_srgb,var(--color-accent)_70%,transparent)] ring-offset-2"
            : "")
        }
      >
        {/* Edit mode: remove badge */}
        {editing && !confirming && (
          <button
            className="tap-target absolute -top-2 -left-2 z-20 w-6 h-6 flex items-center justify-center
                     rounded-full bg-[var(--color-danger)] text-[var(--color-text-inverse)]
                     shadow-sm hover:opacity-90 transition-opacity"
            aria-label="Remove widget"
            onClick={() => setConfirming(true)}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        {entry ? (
          <ErrorBoundary fallback={<ItemFallback />}>
            {React.createElement(entry.component)}
          </ErrorBoundary>
        ) : null}
        {/* Delete confirmation overlay */}
        {confirming && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-between px-4
                     rounded-[var(--radius-md)] bg-[color-mix(in_srgb,var(--color-danger)_10%,transparent)]
                     border border-[color-mix(in_srgb,var(--color-danger)_30%,transparent)] backdrop-blur-sm
                     animate-[fadeIn_150ms_ease-out]"
          >
            <span className="text-sm font-medium text-[var(--color-danger)]">
              Remove this widget?
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 text-sm font-medium rounded-md
                         border border-[var(--color-border)] bg-[var(--color-bg-secondary)]
                         text-[var(--color-text-primary)]
                         hover:bg-[var(--color-bg-hover)] transition-colors"
                onClick={() => setConfirming(false)}
              >
                Keep
              </button>
              <button
                className="px-3 py-1.5 text-sm font-medium rounded-md
                         bg-[var(--color-danger)] text-[var(--color-text-inverse)]
                         hover:opacity-90 transition-colors"
                onClick={() => {
                  setConfirming(false);
                  onRemove();
                }}
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StartDashboard;
